import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { AppVisitNote } from 'src/app/models/data/appVisit';
import { ApiService } from 'src/app/services/api.services';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { DndDirective } from '../../../directives/dnd.directive';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
	selector: 'app-add-device-note-details',
	templateUrl: './add-device-note-details.component.html',
	styleUrls: [
		'./add-device-note-details.component.scss',
		'../../../ui/common/main.scss',
	],
	imports: [
		MatFormField,
		MatInput,
		FormsModule,
		DndDirective,
		MatIcon,
		MatTooltip,
	],
})
export class AddDeviceNoteDetailsComponent {
	@Input() deviceId!: string;
	@Input() appVisitNote!: AppVisitNote;
	@Input() requiredFileType!: string;

	fileDetails: {
		file: File;
		previewURL: string | ArrayBuffer | null;
		caption: string;
	}[] = [];
	allowedTypes = ['image/jpeg', 'image/png'];
	maxFileSize = 2 * 1024 * 1024; // 2MB

	uploadPhotoTooltip = $localize`:@@addDeviceNoteDetails-uploadPhotoTooltip:Upload Photo`;
	photoTooltip = $localize`:@@addDeviceNoteDetails-photoTooltip:Photo`;
	removePhotoTooltip = $localize`:@@addDeviceNoteDetails-removePhotoTooltip:Remove Photo`;

	constructor() {}

	public get isValid(): boolean {
		// Check if the note field is not empty or there is atleast 1 valid photo selected
		if (
			(this.appVisitNote?.note && this.appVisitNote.note !== '') ||
			this.fileDetails.length > 0
		) {
			for (const fileDetail of this.fileDetails) {
				if (
					!this.isFileTypeValid(fileDetail.file) ||
					!this.isFileSizeValid(fileDetail.file)
				) {
					// Check if any file has invalid type or size
					return false;
				}
			}
			return true;
		}
		return false;
	}

	public get selectedFileDetails(): {
		file: File;
		previewURL: string | ArrayBuffer | null;
		caption: string;
	}[] {
		return this.fileDetails;
	}

	onFileSelected(event: any) {
		let input!: any;
		if (Array.isArray(event)) {
			input = event;
		} else {
			input = event.target as HTMLInputElement;
		}
		if (input.files && input.files.length > 0) {
			for (var i = 0; i < input.files.length; i++) {
				const file = input.files[i];
				this.getPreviewUrl(file)
					.then((previewUrl) => {
						const newFileDetail = {
							file: file,
							previewURL: previewUrl,
							caption: '',
						};
						this.fileDetails.push(newFileDetail);
					})
					.catch((error) => {
						console.error('Error getting preview URL:', error);
					});
			}
		} else if (input.length > 0) {
			// this is for drag and drop
			for (var i = 0; i < input.length; i++) {
				const file = input[i].file;
				this.getPreviewUrl(file)
					.then((previewUrl) => {
						const newFileDetail = {
							file: file,
							previewURL: previewUrl,
							caption: '',
						};
						this.fileDetails.push(newFileDetail);
					})
					.catch((error) => {
						console.error('Error getting preview URL:', error);
					});
			}
		}
	}

	getPreviewUrl(file: File): Promise<string | ArrayBuffer | null> {
		return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				if (e.target && e.target.result) {
					resolve(e.target.result);
				}
			};
			reader.readAsDataURL(file);
		});
	}

	isFileTypeValid(file: File): boolean {
		return this.allowedTypes.includes(file.type);
	}

	isFileSizeValid(file: File): boolean {
		return file.size <= this.maxFileSize;
	}

	removeFile(file: File) {
		const index = this.fileDetails.findIndex(
			(fileDetail) => fileDetail.file === file
		);
		if (index !== -1) {
			this.fileDetails.splice(index, 1);
		}
	}
}
